import {
  Container,
  Box,
  Alert,
  Snackbar,
  Backdrop,
  CircularProgress,
  Typography,
  Paper,
  Icon,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  InputAdornment,
  TextField,
  MenuItem,
  Grid,
  Divider,
  LinearProgress,
  Card,
} from "@mui/material";
import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import CheckoutFooter from "../components/footer";
import AppConfig from "../../appConfig";
import axios from "../../axios";
import usePageTitle from "../pageTitle";
import FormSectionBuilder from "../components/data_forms/form_section_builder";
import { useGeolocated } from "react-geolocated";
import DataFormComponent from "../components/data_forms/data_form";
import { useAwaitDialog } from "../components/status_dialog";
import PaymentInstrumentDialog from "../components/data_forms/payment_instrument_dialog";

function DataCapturePage() {
  const [pageTitle, setPageTitle] = useState("Data Capture");
  usePageTitle(pageTitle);

  const { ref } = useParams();
  const navigate = useNavigate();

  const statusDialog = useAwaitDialog();
  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const dataFormRef = useRef();

  const [banner, setBanner] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [presetAmount, setPresetAmount] = useState(null);
  const [dataForm, setDataForm] = useState(null);
  const [dataFormSections, setDataFormSections] = useState([]);
  const [selectedAmount, setAmount] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);

  const [statusMsg, setStatusMsg] = useState("");
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
  const [openErrorDialog, setOpenErrorDialog] = useState(false);

  const [sellResult, setSellResult] = useState(null);
  const [showInlineLoading, setShowInlineLoading] = useState(false);

  const [paymentInstrument, setPaymentInstrument] = useState(null);

  const [renderCount, setRenderCount] = useState(0);
  let step = 0;

  useEffect(() => {
    if (ref === null) {
      navigate("/error");
      alert("NONE");
      return;
    }

    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(`data_captures/${ref}/fetch.json`)
        .then((result) => {
          // console.log(result);
          setShowLoading(false);
          if (result.data.status === "success") {
            // console.log(result.data.data.campaign.data_form);
            setBanner(result.data.data.banner);
            setCampaign(result.data.data.campaign);
            setDataForm(result.data.data.campaign.data_form);
            setPresetAmount(result.data.data.preset_amount);
            if (result.data.data.preset_amount !== null) {
              if (result.data.data.preset_amount.amount_type === "FIXED") {
                setAmount(result.data.data.preset_amount.amount / 100);
              }
            }
            setPageTitle(
              `${result.data.data.campaign.account.name} | ${result.data.data.campaign.title}`,
            );

            // Handle App Update
            const currentVersion = localStorage.getItem(
              AppConfig.app_version_store,
            );
            const newVersion = result.data.version;
            if (currentVersion !== newVersion) {
              localStorage.setItem(AppConfig.app_version_store, newVersion);
              if (
                window.confirm("New version available. Reloading...") === true
              ) {
                window.location.reload(true);
              } else {
                window.location.reload(true);
              }
              return;
            }
          } else {
            // setSeverity("error");
            // setErrMsg(result.data.data.message);
            // setOpen(true);
            navigate("/error");
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine
                ? ""
                : "Make sure you have an active internet connection"
            }`,
          );
          setOpen(true);
        });
    };

    fetchData();
  }, []);

  const { coords, isGeolocationAvailable, isGeolocationEnabled } =
    useGeolocated({
      positionOptions: {
        enableHighAccuracy: true,
      },
      userDecisionTimeout: 5000,
    });

  function getGPSCoords() {
    if (!isGeolocationAvailable) {
      return false;
    }

    if (!isGeolocationEnabled) {
      return false;
    }

    if (coords) {
      return { latitude: coords.latitude, longitude: coords.longitude };
    }

    return false;
  }

  async function capture() {
    let lat = null;
    let lng = null;
    const gpsCoords = getGPSCoords();
    if (campaign.enforce_location_capture) {
      if (!gpsCoords) {
        setSeverity("error");
        setErrMsg("Please enable and allow browser's location and GPS access.");
        setOpen(true);
        return;
      }
    }

    // Collect if captured
    if (gpsCoords) {
      lat = gpsCoords.latitude;
      lng = gpsCoords.longitude;
    }

    // Get form data
    let formValues = null;
    if (dataForm !== null) {
      const formResult = dataFormRef.current.submit();
      if (formResult.error) {
        setSeverity("error");
        setErrMsg(formResult.error);
        setOpen(true);
        return;
      }
      formValues = formResult.values;
    }

    // console.log(formValues);
    // return;

    let pAmount = selectedAmount;
    if (presetAmount !== null) {
      const amt = parseInt(selectedAmount, 10);
      if (presetAmount.amount_type === "VARIABLE") {
        if (presetAmount.minimum_amount) {
          if (amt < presetAmount.minimum_amount / 100) {
            setSeverity("error");
            setErrMsg("Amount cannot be less than the minimum amount.");
            setOpen(true);
            return;
          }
        }

        if (presetAmount.maximum_amount) {
          if (amt > presetAmount.maximum_amount / 100) {
            setSeverity("error");
            setErrMsg("Amount cannot exceed the maximum amount.");
            setOpen(true);
            return;
          }
        }

        pAmount = amt * 100;
      }

      if (paymentMethod === null) {
        setSeverity("error");
        setErrMsg("Payment method is required.");
        setOpen(true);
        return;
      }
    }

    if (window.confirm("Are you sure you want to proceed?") === false) {
      return;
    }

    setShowLoading(true);
    const json = {
      reference: ref,
      campaign: campaign.reference,
      form: dataForm.reference,
      data: formValues,
      latitude: lat,
      longitude: lng,
      amount: pAmount,
      payment_method: paymentMethod,
    };

    await axios
      .post("data_captures/capture.json", json)
      .then(async (result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          if (result.data.data.status === "success") {
            await statusDialog.show(result.data.data.message, "success");
            window.location.reload();
          }

          if (result.data.data.status === "pending") {
            setPaymentInstrument(result.data.data);
          }
        } else {
          await statusDialog.show(result.data.data.message, "error", "Failed");
        }
      })
      .catch(async function () {
        setShowLoading(false);
        await statusDialog.show(
          "Unable to complete request.",
          "error",
          "Failed",
        );
      });
  }

  return (
    <>
      <Box mb={5}>
        <Container>
          <Backdrop
            sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={showLoading}
            onClick={handleClose}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
          <Snackbar
            open={open}
            autoHideDuration={6000}
            onClose={handleClose}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert
              onClose={handleClose}
              severity={severity}
              sx={{ width: "100%" }}
            >
              {errorMsg}
            </Alert>
          </Snackbar>
        
          {paymentInstrument && (
            <PaymentInstrumentDialog paymentDetails={paymentInstrument} />
          )}

          {campaign !== null && (
            <Box sx={{ margin: "5px auto", maxWidth: "600px" }}>
              <Box>
                <Paper elevation={0}>
                  <Box p={2} align="center">
                    <Box>
                      <img
                        src={`${AppConfig.api_media_url}${banner}`}
                        alt="img"
                        style={{ maxWidth: "40%", maxHeight: "100%" }}
                      />
                    </Box>
                    <Box mt={3}>
                      <Typography variant="subtitle1" fontWeight={500}>
                        {campaign.account.name}
                      </Typography>
                    </Box>
                    <Box mt={1}>
                      <Typography variant="caption" fontWeight={350}>
                        {campaign.title}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Box>
              {campaign.description.length > 0 && (
                <Box mt={3}>
                  <Paper elevation={3}>
                    <Box p={2}>
                      <Box>
                        <Typography variant="caption" fontWeight="bold">
                          DESCRIPTION
                        </Typography>
                      </Box>
                      <Box mt={2}>
                        <Typography variant="caption" fontSize={12}>
                          <div
                            dangerouslySetInnerHTML={{
                              __html: campaign.description,
                            }}
                          />
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Box>
              )}

              {dataForm !== null && (
                <Box mt={3}>
                  <DataFormComponent ref={dataFormRef} dataForm={dataForm} />
                </Box>
              )}

              {presetAmount !== null && (
                <Box mt={5}>
                  <Paper elevation={3}>
                    <Box px={3} py={3}>
                      <Box>
                        <Typography variant="caption" fontWeight="bold">
                          PAYMENT INFORMATION
                        </Typography>
                        {presetAmount.amount_type === "FIXED" ? (
                          <Box mt={2} mb={3}>
                            <TextField
                              name="Amount"
                              margin="dense"
                              label={presetAmount.display_name}
                              readonly
                              fullWidth
                              variant="outlined"
                              value={selectedAmount.toLocaleString("en", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    {presetAmount.currency}
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Box>
                        ) : (
                          <Box mt={2} mb={3}>
                            <TextField
                              name="Amount"
                              margin="dense"
                              label={presetAmount.display_name}
                              placeholder="Enter an amount"
                              type="decimal"
                              fullWidth
                              variant="outlined"
                              onChange={(e) => {
                                setAmount(e.target.value);
                              }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    {presetAmount.currency}
                                  </InputAdornment>
                                ),
                              }}
                            />
                            <Box mt={0.5}>
                              <Grid container spacing={3}>
                                <Grid item xs={6} md={6} lg={6}>
                                  {presetAmount.minimum_amount !== null && (
                                    <Typography
                                      align="left"
                                      fontSize={10}
                                      px={1}
                                    >
                                      {`Minimum: ${presetAmount.currency}
                                      ${(
                                        presetAmount.minimum_amount / 100
                                      ).toLocaleString("en", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })}`}
                                    </Typography>
                                  )}
                                </Grid>
                                <Grid item xs={6} md={6} lg={6}>
                                  {presetAmount.maximum_amount !== null && (
                                    <Typography
                                      align="right"
                                      fontSize={10}
                                      px={1}
                                    >
                                      {`Maximum: ${presetAmount.currency}
                                      ${(
                                        presetAmount.maximum_amount / 100
                                      ).toLocaleString("en", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })}`}
                                    </Typography>
                                  )}
                                </Grid>
                              </Grid>
                            </Box>
                          </Box>
                        )}
                      </Box>
                      <Box mt={5} mb={3}>
                        <Typography variant="caption" fontWeight="bold">
                          PAYMENT METHOD
                        </Typography>
                        <Box mt={3}>
                          <TextField
                            fullWidth
                            select
                            label="How do you want to pay?"
                            onChange={(e) => {
                              setPaymentMethod(e.target.value);
                            }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Icon>payment</Icon>
                                </InputAdornment>
                              ),
                            }}
                          >
                            {
                              // --> Notice below changes
                              presetAmount.payment_methods.length > 0 && // --> Changes from `options.length ? ...` to `options.length > 0 && ...`
                                presetAmount.payment_methods.map((record) => (
                                  <MenuItem
                                    key={record.value}
                                    value={record.value}
                                  >
                                    {record.name}
                                  </MenuItem>
                                ))
                            }
                          </TextField>
                        </Box>
                      </Box>
                    </Box>
                  </Paper>
                </Box>
              )}

              <Box mt={7} align="center">
                <Button
                  size="large"
                  sx={{ width: { xs: "100%", md: "100%", lg: "100%" } }}
                  endIcon={<Icon>save</Icon>}
                  color="primary"
                  variant="contained"
                  onClick={() => {
                    capture();
                  }}
                >
                  Submit
                </Button>
              </Box>
            </Box>
          )}
        </Container>
      </Box>
      <CheckoutFooter />
    </>
  );
}

export default DataCapturePage;
